<template>
  <TemplateError :title="title" :subtitle="subtitle" :introduction="introduction" :description="description" :image="image" />
</template>

<script setup lang="ts">
import type { NuxtError } from '#app'
import { useI18n } from 'vue-i18n'

/*
 * Because this is the error page, try to use as little logic as possible: everything the CMS could
 * supply (navigation, translation overrides, the error page's own content) is fetched defensively, since
 * a broken CMS may well be *why* we ended up here. Each fetch keeps the page working (with static
 * fallbacks) rather than turning a 404/500 into a second, harder failure.
 */

defineProps<{ error: NuxtError }>()

const globalStore = useGlobalStore()
const nuxtError = useError()

const statusCodeMap: Record<number, string> = {
  404: 'Sorry, de pagina is niet beschikbaar',
  500: 'Er is helaas iets misgegaan',
}
const statusCode = nuxtError.value?.statusCode || 500

const title = ref(statusCodeMap[statusCode] || statusCodeMap[500])
const subtitle = ref('')
const introduction = ref('')
const description = ref('')
const image = ref(undefined)

// app.vue's <NuxtLayout>/<NuxtPage> chain (and its translation-override merge) does not run for this
// page: a fatal error replaces the whole app root, so redo the merge here.
const i18n = useI18n()
const translationStore = useTranslationStore()
try {
  const locale = i18n.locale.value
  await useAsyncData(`translations:${locale}`, async () => await translationStore.load({ locale }))
  i18n.mergeLocaleMessage(translationStore.locale, translationStore.translations)
} catch {
  // Keep the default static messages.
}

try {
  await globalStore.fetchNavigation()
} catch {
  // Keep the (empty) navigation state; the header/footer render without menu items rather than crash.
}

try {
  const page = await globalStore.fetchErrorPage({ statusCode })
  if (page) {
    subtitle.value = page.subtitle || ''
    introduction.value = page.introduction || ''
    description.value = page.description || ''
    image.value = page.image || undefined
  }
} catch {
  // Keep the static statusCodeMap title with no further content.
}

useHead({ title: title.value })
</script>
